import { useMemo } from "react";
import { Navigate } from "react-router-dom";
import { useQuery } from "@apollo/client";
import Table, { TableHeading, TableFooter } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import MetricGrid from "@/components/MetricGrid";
import AppLayout from "@/layouts/AppLayout";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { useFlags, useFlagsLoaded } from "@/hooks/useFeatureFlags";
import usePageTitle from "@/hooks/usePageTitle";
import { toFormat } from "@/lib/dateHelpers";
import PatVolumeCharts from "./_components/PatVolumeCharts";
import { PAT_RESULT_VOLUME_QUERY, type PatResultVolumeAttemptBucket, type PatResultVolumeYear } from "./queries";
import { formatCount, formatPercent, percentOf } from "./_utils";

const PAGE_PATH = "/events/pat-results";

type PatResultVolumeRow = PatResultVolumeYear & {
  id: string;
  passedPercent: number;
  lookerFailPercent: number;
};

type AttemptRow = PatResultVolumeAttemptBucket & { id: string };

function freshnessCaption(maxBeginDate?: string | null, maxSubmittedAt?: string | null): string | undefined {
  const eventDate = maxBeginDate ? toFormat(maxBeginDate) : null;
  const submittedAt = maxSubmittedAt ? toFormat(maxSubmittedAt, "LLL dd, yyyy, h:mm a") : null;
  if (!eventDate && !submittedAt) return undefined;

  return [
    eventDate ? `Latest event date ${eventDate}` : null,
    submittedAt ? `Latest score submitted ${submittedAt}` : null
  ]
    .filter(Boolean)
    .join(" · ");
}

function CountCell({ getValue }: { getValue: () => unknown }) {
  return formatCount(Number(getValue()));
}

function PercentCell({ getValue }: { getValue: () => unknown }) {
  return formatPercent(Number(getValue()));
}

export default function PatResultsPage() {
  usePageTitle(PAGE_PATH, "PAT Results");
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.editPAT });

  const flags = useFlags();
  const flagsLoaded = useFlagsLoaded();
  const isPatResultVolumeEnabled = flags["backoffice-pat-result-volume"] === true;

  const { data, loading, error } = useQuery(PAT_RESULT_VOLUME_QUERY, {
    skip: !flagsLoaded || !isPatResultVolumeEnabled
  });
  const volume = data?.patResultVolume;
  const years = volume?.years ?? [];
  const passedByAttempt = volume?.passedByAttempt ?? [];
  const currentYear = new Date().getFullYear();
  const currentYearRow = years.find((row) => row.year === currentYear);
  const caption = freshnessCaption(volume?.maxBeginDate, volume?.maxSubmittedAt);
  const windowPassed = years.reduce((sum, row) => sum + row.passedCount, 0);
  const windowScored = years.reduce((sum, row) => sum + row.scoredCount, 0);
  const tableRows: PatResultVolumeRow[] = years.map((year) => ({
    ...year,
    id: String(year.year),
    passedPercent: percentOf(year.passedCount, year.scoredCount),
    lookerFailPercent: percentOf(year.lookerFailCount, year.scoredCount)
  }));
  const attemptRows: AttemptRow[] = passedByAttempt.map((bucket) => ({ ...bucket, id: bucket.label }));

  const attemptColumns = useMemo(
    () => [
      { accessorKey: "label", header: "Attempts", enableSorting: false },
      { accessorKey: "passedPeopleCount", header: "People passed", enableSorting: false, cell: CountCell }
    ],
    []
  );

  const yearColumns = useMemo(
    () => [
      { accessorKey: "year", header: "Year", enableSorting: false },
      { accessorKey: "passedCount", header: "Passed", enableSorting: false, cell: CountCell },
      { accessorKey: "passedPercent", header: "Pass %", enableSorting: false, cell: PercentCell },
      { accessorKey: "failedCount", header: "Failed", enableSorting: false, cell: CountCell },
      { accessorKey: "qualifiedCount", header: "Qualified", enableSorting: false, cell: CountCell },
      { accessorKey: "lookerFailCount", header: "Failed + Qualified", enableSorting: false, cell: CountCell },
      { accessorKey: "lookerFailPercent", header: "Fail %", enableSorting: false, cell: PercentCell },
      { accessorKey: "scoredCount", header: "Scored", enableSorting: false, cell: CountCell },
      { accessorKey: "neverSyncedCount", header: "Not in CCMS", enableSorting: false, cell: CountCell }
    ],
    []
  );

  const peopleColumns = useMemo(
    () => [
      { accessorKey: "year", header: "Year", enableSorting: false },
      { accessorKey: "distinctPassedPeopleCount", header: "People passed", enableSorting: false, cell: CountCell },
      {
        accessorKey: "distinctFailedPeopleCount",
        header: "People failed + qualified",
        enableSorting: false,
        cell: CountCell
      }
    ],
    []
  );

  if (!flagsLoaded) {
    return null;
  }

  if (!isPatResultVolumeEnabled) {
    return <Navigate to="/events" replace />;
  }

  if (error) {
    return (
      <AppLayout>
        <EmptyState title="Error" caption="An error occurred while loading PAT results." iconName="error" />
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <TableHeading
        heading="PAT Results"
        body="Year-over-year Playing Ability Test volume from M360 plus CCMS historical PAT credits that were never stored in M360. Dual-written MEM360 copies are counted once. Years use the event date (begin_date). Fail is Failed + Qualified (pf_flag F). Registration timing uses the person's earliest membership band start."
        isPageHeading
      />
      <MetricGrid
        hasHeading
        metrics={[
          {
            label: `${currentYear} passed`,
            value: loading && !currentYearRow ? undefined : formatCount(currentYearRow?.passedCount),
            loading,
            iconName: "check_circle",
            testId: "pat-results-passed-metric",
            tooltip: {
              supportingText: "PAT submissions with result Passed in the current event-date year."
            }
          },
          {
            label: `${currentYear} failed + qualified`,
            value: loading && !currentYearRow ? undefined : formatCount(currentYearRow?.lookerFailCount),
            loading,
            iconName: "cancel",
            testId: "pat-results-fail-metric",
            tooltip: {
              supportingText: "Failed plus Qualified. Looker counts both as pf_flag F."
            }
          },
          {
            label: `${currentYear} not in CCMS`,
            value: loading && !currentYearRow ? undefined : formatCount(currentYearRow?.neverSyncedCount),
            loading,
            iconName: "cloud_off",
            testId: "pat-results-unsynced-metric",
            tooltip: {
              supportingText: "Scored M360 submissions in the current year with no CCMS legacy id."
            }
          },
          {
            label: "10-year pass %",
            value: loading && years.length === 0 ? undefined : formatPercent(percentOf(windowPassed, windowScored)),
            loading,
            iconName: "bar_chart",
            testId: "pat-results-window-pass-percent-metric",
            tooltip: {
              supportingText: "Passed scored attempts across the years on this page, matching Looker's pass percentage."
            }
          },
          {
            label: "Passed before membership start",
            value: loading && !volume ? undefined : formatCount(volume?.firstPassBeforeMembershipStartCount),
            loading,
            iconName: "history",
            testId: "pat-results-before-membership-metric",
            tooltip: {
              supportingText:
                "People whose first PAT pass is before their earliest membership band start. Looker called this passed before registration."
            }
          },
          {
            label: "Passed on/after membership start",
            value: loading && !volume ? undefined : formatCount(volume?.firstPassAfterMembershipStartCount),
            loading,
            iconName: "event",
            testId: "pat-results-after-membership-metric",
            tooltip: {
              supportingText:
                "People whose first PAT pass is on or after their earliest membership band start. Looker called this passed after registration."
            }
          },
          {
            label: "CCMS historical in range",
            value: loading && !volume ? undefined : formatCount(volume?.ccmsHistoricalScoredCount),
            loading,
            iconName: "archive",
            testId: "pat-results-ccms-historical-metric",
            tooltip: {
              supportingText:
                "Scored CCMS PAT credits in these years that are not already M360 submissions. Dual-written MEM360 rows are excluded."
            }
          }
        ]}
      />
      {caption && (
        <p className="text-body-medium text-sys-brand-on-surface-variant mt-4" data-testid="pat-results-freshness">
          {caption}
        </p>
      )}
      <PatVolumeCharts years={years} passedByAttempt={passedByAttempt} loading={loading} />
      <TableHeading
        heading="Attempts by year"
        body="Scored PAT attempts in each event-date year from M360 and CCMS history, including pass and fail percentages."
      />
      <Table
        data={tableRows}
        columns={yearColumns}
        loading={loading}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No PAT Results in This Range."
            caption="There are no PAT attempts in the last 10 years."
            iconName="golf_course"
          />
        )}
        renderFooter={() => <TableFooter totalRowCount={tableRows.length} />}
      />
      <TableHeading
        heading="People by year"
        body="Distinct people with a scored M360 or CCMS result in that event-date year. A person who attempts more than once can appear in Fail without appearing in Pass."
      />
      <Table
        data={tableRows}
        columns={peopleColumns}
        loading={loading}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No PAT People in This Range."
            caption="There are no scored people in the last 10 years."
            iconName="group"
          />
        )}
        renderFooter={() => <TableFooter totalRowCount={tableRows.length} />}
      />
      <TableHeading
        heading="Passed by number of attempts"
        body="People whose first pass is in this year range, counted by scored attempts through that pass."
      />
      <Table
        data={attemptRows}
        columns={attemptColumns}
        loading={loading}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No First Passes in This Range."
            caption="There are no first-time PAT passes in the last 10 years."
            iconName="golf_course"
          />
        )}
        renderFooter={() => <TableFooter totalRowCount={attemptRows.length} />}
      />
    </AppLayout>
  );
}
